import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2, Calendar, Phone, MapPin, Building2, Clock, Zap, List, CalendarDays, Navigation } from 'lucide-react';
import { toast } from 'react-hot-toast';
import CreneauxDisponibles from './CreneauxDisponibles';
import SirenSearch from './SirenSearch';
import ProspectCalendar from './ProspectCalendar';

const API = process.env.REACT_APP_BACKEND_URL;

const ProspectsPage = () => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRdvDialog, setShowRdvDialog] = useState(false);
  const [showCreneauxDialog, setShowCreneauxDialog] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);
  const [selectedProspectForRdv, setSelectedProspectForRdv] = useState(null);
  const [viewMode, setViewMode] = useState('agenda'); // 'list' ou 'agenda'
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [optimAddress, setOptimAddress] = useState('');
  const [optimSuggestions, setOptimSuggestions] = useState([]);
  const [optimCoords, setOptimCoords] = useState(null);
  const [userFilter, setUserFilter] = useState('');
  const [commercialFilter, setCommercialFilter] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [allCommerciaux, setAllCommerciaux] = useState([]);
  const [prospectSources, setProspectSources] = useState([]);
  
  // États pour la recherche et les filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('tous');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  
  const [formData, setFormData] = useState({
    adresse: '',
    telephone: '',
    nom: '',
    prenom: '',
    nom_entreprise: '',
    siren: '',
    email: '',
    type_energie: '',
    consommation_annuelle_euro: '',
    consommation_annuelle_kwh: '',
    fournisseur_actuel: '',
    notes: '',
    notes_commercial: '',
    source_prospect: '',
    latitude: null,
    longitude: null,
    code_postal: '',
    ville: '',
    // 🆕 NOUVEAUX CHAMPS
    type_contrat_travail: '',
    domaine_activite: '',
    situation_familiale: '',
    montant_credit_en_cours: '',
    duree_rdv_minutes: 60,
    mode_chauffage_actuel: '',
    // 🆕 CHAMPS MONSIEUR/MADAME
    type_contrat_monsieur: '',
    type_contrat_madame: '',
    revenus_mensuels_monsieur: '',
    revenus_mensuels_madame: '',
    // 🆕 CHAMPS TOITURE & QUALIFICATION
    nombre_m2_toit: '',
    velux_chien_assis: '',
    type_toiture: '',
    orientation_toit: '',
    zone_classee: '',
    nombre_enfants: '',
    age_monsieur: '',
    age_madame: '',
    montant_credit_immobilier: '',
    autre_credit: ''
  });

  // États pour le formulaire RDV
  const [rdvFormData, setRdvFormData] = useState({
    prospect_id: '',
    date_rdv: '',
    duree_minutes: 60,
    type_rdv: 'visite_commerciale',
    status: 'planifie',
    commentaire_rdv: ''
  });

  // États pour la recherche d'adresse (réutiliser la logique existante)
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

  // États pour la recherche SIREN (réutiliser la logique existante)
  const [sirenSuggestions, setSirenSuggestions] = useState([]);
  const [showSirenSuggestions, setShowSirenSuggestions] = useState(false);
  const [isLoadingSiren, setIsLoadingSiren] = useState(false);

  const [rendezVousList, setRendezVousList] = useState([]);

  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error getting current user:', error);
    } finally {
      setUserLoaded(true);
    }
    fetchAllData();
    
    // Check URL params for pre-fill from CreneauxOptimisesPage
    const params = new URLSearchParams(window.location.search);
    if (params.get('create') === 'true') {
      const dateRdv = params.get('date_rdv');
      const addr = params.get('address');
      const cp = params.get('cp');
      const vi = params.get('ville');
      if (dateRdv) setRdvFormData(prev => ({ ...prev, date_rdv: dateRdv }));
      if (addr) setFormData(prev => ({ ...prev, adresse: addr, code_postal: cp || '', ville: vi || '' }));
      setShowCreateDialog(true);
      window.history.replaceState({}, '', '/prospects');
    }
  }, []);

  // Charger les users quand le currentUser est connu
  useEffect(() => {
    if (currentUser?.role === 'admin' || currentUser?.role === 'confirmateur' || currentUser?.role === 'administratif') {
      const token = localStorage.getItem('token');
      axios.get(`${API}/api/users`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setAllUsers((res.data || []).filter(u => u.role === 'telepro' || u.role === 'confirmateur' || u.role === 'superviseur'));
          setAllCommerciaux((res.data || []).filter(u => u.role === 'commercial' || u.role === 'regie'));
        }).catch(() => {});
    }
  }, [currentUser]);

  // Chargement principal — TOUT en une fois, puis affichage d'un coup
  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [prospRes, srcRes] = await Promise.all([
        axios.get(`${API}/api/prospects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/app-config/prospect_sources`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      setProspects(prospRes.data);
      setProspectSources(srcRes.data?.sources || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Charger les RDV seulement quand nécessaire (vue Liste avec filtre statut RDV)
  const loadRdvIfNeeded = async () => {
    if (rendezVousList.length > 0) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/rendez-vous`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }));
      setRendezVousList(res.data || []);
    } catch (e) {}
  };
  
  const fetchProspects = fetchAllData;

  const geocodeAddress = async (adresse, codePostal, ville) => {
    try {
      const fullAddress = `${adresse}, ${codePostal} ${ville}, France`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        return {
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon)
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  const handleCreateProspect = async () => {
    if (isSubmitting) return;
    try {
      if (!formData.adresse || !formData.telephone) {
        toast.error('Adresse et téléphone sont obligatoires');
        return;
      }
      if (!formData.source_prospect) {
        toast.error('Source Prospect est obligatoire');
        return;
      }
      setIsSubmitting(true);

      // Géolocalisation automatique
      const coords = await geocodeAddress(formData.adresse, formData.code_postal, formData.ville);

      // 🔧 NETTOYER LES CHAMPS NUMÉRIQUES VIDES - Convertir chaînes vides en null
      const parseNumericField = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
      };

      const cleanData = {
        ...formData,
        // Champs numériques obligatoires avec valeur par défaut
        consommation_annuelle_euro: parseNumericField(formData.consommation_annuelle_euro) || 0.0,
        consommation_annuelle_kwh: parseNumericField(formData.consommation_annuelle_kwh) || 0.0,
        // Champs numériques optionnels (peuvent être null)
        montant_credit_en_cours: parseNumericField(formData.montant_credit_en_cours),
        revenus_mensuels_monsieur: parseNumericField(formData.revenus_mensuels_monsieur),
        revenus_mensuels_madame: parseNumericField(formData.revenus_mensuels_madame),
        nombre_m2_toit: parseNumericField(formData.nombre_m2_toit),
        nombre_enfants: parseNumericField(formData.nombre_enfants),
        age_monsieur: parseNumericField(formData.age_monsieur),
        age_madame: parseNumericField(formData.age_madame),
        montant_credit_immobilier: parseNumericField(formData.montant_credit_immobilier),
        // Coordonnées
        latitude: coords?.latitude || 0.0,
        longitude: coords?.longitude || 0.0,
        // Si un RDV est planifié, mettre le statut prospect à rdv_planifie
        ...(rdvFormData.date_rdv ? { status: 'rdv_planifie' } : {})
      };

      const token = localStorage.getItem('token');
      const prospectRes = await axios.post(`${API}/api/prospects`, cleanData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // 🆕 Si date de RDV remplie, créer automatiquement le RDV
      if (rdvFormData.date_rdv) {
        try {
          await axios.post(`${API}/api/rendez-vous`, {
            prospect_id: prospectRes.data.id,
            date_rdv: rdvFormData.date_rdv,
            duree_minutes: parseInt(rdvFormData.duree_minutes) || 60,
            type_rdv: rdvFormData.type_rdv || 'visite_commerciale',
            status: 'planifie',
            commentaire_rdv: rdvFormData.commentaire_rdv || ''
          }, { headers: { Authorization: `Bearer ${token}` } });
          toast.success('Prospect et RDV créés avec succès !');
        } catch (rdvErr) {
          console.error('Error creating RDV:', rdvErr);
          toast.success('Prospect créé, mais erreur lors de la création du RDV');
        }
      } else {
        toast.success('Prospect créé avec succès');
      }

      setShowCreateDialog(false);
      resetForm();
      fetchProspects();
      setCalendarRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error creating prospect:', error);
      toast.error('Erreur lors de la création du prospect');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProspect = async () => {
    try {
      // Géolocalisation automatique
      const coords = await geocodeAddress(formData.adresse, formData.code_postal, formData.ville);

      const parseNumericField = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
      };

      const cleanData = {
        ...formData,
        consommation_annuelle_euro: parseNumericField(formData.consommation_annuelle_euro) || 0.0,
        consommation_annuelle_kwh: parseNumericField(formData.consommation_annuelle_kwh) || 0.0,
        montant_credit_en_cours: parseNumericField(formData.montant_credit_en_cours),
        revenus_mensuels_monsieur: parseNumericField(formData.revenus_mensuels_monsieur),
        revenus_mensuels_madame: parseNumericField(formData.revenus_mensuels_madame),
        nombre_m2_toit: parseNumericField(formData.nombre_m2_toit),
        nombre_enfants: parseNumericField(formData.nombre_enfants),
        age_monsieur: parseNumericField(formData.age_monsieur),
        age_madame: parseNumericField(formData.age_madame),
        montant_credit_immobilier: parseNumericField(formData.montant_credit_immobilier),
        latitude: coords?.latitude || 0.0,
        longitude: coords?.longitude || 0.0
      };

      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/prospects/${editingProspect.id}`, cleanData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Mettre à jour le RDV si date renseignée
      if (rdvFormData.date_rdv && rdvFormData._rdv_id) {
        try {
          await axios.put(`${API}/api/rendez-vous/${rdvFormData._rdv_id}`, {
            date_rdv: rdvFormData.date_rdv,
            duree_minutes: parseInt(rdvFormData.duree_minutes) || 60,
            type_rdv: rdvFormData.type_rdv,
            commentaire_rdv: rdvFormData.commentaire_rdv || ''
          }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (e) { console.error('Error updating RDV:', e); }
      } else if (rdvFormData.date_rdv && !rdvFormData._rdv_id) {
        // Créer un nouveau RDV si date renseignée mais pas de RDV existant
        try {
          await axios.post(`${API}/api/rendez-vous`, {
            prospect_id: editingProspect.id,
            date_rdv: rdvFormData.date_rdv,
            duree_minutes: parseInt(rdvFormData.duree_minutes) || 60,
            type_rdv: rdvFormData.type_rdv || 'visite_commerciale',
            status: 'planifie',
            commentaire_rdv: rdvFormData.commentaire_rdv || ''
          }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (e) { console.error('Error creating RDV:', e); }
      }

      toast.success('Prospect modifié avec succès');
      setShowEditDialog(false);
      setEditingProspect(null);
      resetForm();
      fetchProspects();
      setCalendarRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error updating prospect:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeleteProspect = async (prospectId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce prospect ?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/prospects/${prospectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Prospect supprimé avec succès');
      fetchProspects();
    } catch (error) {
      console.error('Error deleting prospect:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      adresse: '',
      telephone: '',
      nom: '',
      prenom: '',
      nom_entreprise: '',
      siren: '',
      email: '',
      type_energie: '',
      consommation_annuelle_euro: '',
      consommation_annuelle_kwh: '',
      fournisseur_actuel: '',
      notes: '',
      notes_commercial: '',
      source_prospect: '',
      latitude: null,
      longitude: null,
      code_postal: '',
      ville: '',
      type_contrat_travail: '',
      domaine_activite: '',
      situation_familiale: '',
      montant_credit_en_cours: '',
      duree_rdv_minutes: 60,
      mode_chauffage_actuel: '',
      type_contrat_monsieur: '',
      type_contrat_madame: '',
      revenus_mensuels_monsieur: '',
      revenus_mensuels_madame: '',
      nombre_m2_toit: '',
      velux_chien_assis: '',
      type_toiture: '',
      orientation_toit: '',
      zone_classee: '',
      nombre_enfants: '',
      age_monsieur: '',
      age_madame: '',
      montant_credit_immobilier: '',
      autre_credit: ''
    });
    setRdvFormData({
      prospect_id: '',
      date_rdv: '',
      duree_minutes: 60,
      type_rdv: 'visite_commerciale',
      status: 'planifie',
      commentaire_rdv: ''
    });
  };

  const openEditDialog = async (prospect) => {
    setEditingProspect(prospect);
    setFormData({
      adresse: prospect.adresse,
      telephone: prospect.telephone,
      nom: prospect.nom || '',
      prenom: prospect.prenom || '',
      nom_entreprise: prospect.nom_entreprise || '',
      siren: prospect.siren || '',
      email: prospect.email || '',
      type_energie: prospect.type_energie || '',
      consommation_annuelle_euro: prospect.consommation_annuelle_euro || '',
      consommation_annuelle_kwh: prospect.consommation_annuelle_kwh || '',
      fournisseur_actuel: prospect.fournisseur_actuel || '',
      notes: prospect.notes || '',
      notes_commercial: prospect.notes_commercial || '',
      source_prospect: prospect.source_prospect || '',
      latitude: prospect.latitude || null,
      longitude: prospect.longitude || null,
      code_postal: prospect.code_postal || '',
      ville: prospect.ville || '',
      type_contrat_travail: prospect.type_contrat_travail || '',
      domaine_activite: prospect.domaine_activite || '',
      situation_familiale: prospect.situation_familiale || '',
      montant_credit_en_cours: prospect.montant_credit_en_cours || '',
      duree_rdv_minutes: prospect.duree_rdv_minutes || 60,
      mode_chauffage_actuel: prospect.mode_chauffage_actuel || '',
      type_contrat_monsieur: prospect.type_contrat_monsieur || '',
      type_contrat_madame: prospect.type_contrat_madame || '',
      revenus_mensuels_monsieur: prospect.revenus_mensuels_monsieur || '',
      revenus_mensuels_madame: prospect.revenus_mensuels_madame || '',
      nombre_m2_toit: prospect.nombre_m2_toit || '',
      velux_chien_assis: prospect.velux_chien_assis || '',
      type_toiture: prospect.type_toiture || '',
      orientation_toit: prospect.orientation_toit || '',
      zone_classee: prospect.zone_classee || '',
      nombre_enfants: prospect.nombre_enfants || '',
      age_monsieur: prospect.age_monsieur || '',
      age_madame: prospect.age_madame || '',
      montant_credit_immobilier: prospect.montant_credit_immobilier || '',
      autre_credit: prospect.autre_credit || ''
    });
    // Charger le RDV associé au prospect
    try {
      const token = localStorage.getItem('token');
      const rdvRes = await axios.get(`${API}/api/rendez-vous`, { headers: { Authorization: `Bearer ${token}` } });
      const prospectRdv = rdvRes.data.find(r => r.prospect_id === prospect.id);
      if (prospectRdv) {
        const d = new Date(prospectRdv.date_rdv);
        const localDate = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
        setRdvFormData({
          prospect_id: prospect.id,
          date_rdv: localDate,
          duree_minutes: prospectRdv.duree_minutes || 60,
          type_rdv: prospectRdv.type_rdv || 'visite_commerciale',
          status: prospectRdv.status || 'planifie',
          commentaire_rdv: prospectRdv.commentaire_rdv || '',
          _rdv_id: prospectRdv.id
        });
      } else {
        setRdvFormData({ prospect_id: '', date_rdv: '', duree_minutes: 60, type_rdv: 'visite_commerciale', status: 'planifie', commentaire_rdv: '' });
      }
    } catch (e) {
      console.error('Error loading RDV:', e);
    }
    setShowEditDialog(true);
  };

  // Recherche d'adresse (réutiliser la logique des clients)
  const searchAddresses = async (query) => {
    // 🔧 Nettoyer et valider la requête
    const cleanQuery = query.trim();
    
    if (cleanQuery.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    try {
      // 🔧 NE PAS ENVOYER LE TOKEN D'AUTH AUX APIs EXTERNES !
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(cleanQuery)}&limit=5`
      );
      
      if (!response.ok) {
        // Ignorer silencieusement les erreurs 400 (requête invalide)
        if (response.status === 400) {
          setAddressSuggestions([]);
          setShowAddressSuggestions(false);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setAddressSuggestions(data.features || []);
      setShowAddressSuggestions(true);
    } catch (error) {
      console.error('Error searching addresses:', error);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
  };

  const selectAddress = (address) => {
    const fullAddress = address.properties.label;
    setFormData({
      ...formData,
      adresse: fullAddress,
      code_postal: address.properties.postcode || '',
      ville: address.properties.city || '',
      latitude: address.geometry.coordinates[1] || 0,
      longitude: address.geometry.coordinates[0] || 0
    });
    setShowAddressSuggestions(false);
  };

  // Fonction pour gérer la sélection d'entreprise SIREN
  const handleCompanySelect = (companyData) => {
    setFormData(prevData => ({
      ...prevData,
      siren: companyData.siren,
      nom_entreprise: companyData.raison_sociale,
      adresse: companyData.adresse,
      code_postal: companyData.code_postal,
      ville: companyData.ville,
      // Remplir automatiquement le nom et prénom du dirigeant
      nom: companyData.nom,
      prenom: companyData.prenom
    }));

    toast.success(`Entreprise sélectionnée: ${companyData.raison_sociale}`);
  };

  // Recherche SIREN (réutiliser la logique des clients)
  const searchSiren = async (query) => {
    if (query.length < 3) {
      setSirenSuggestions([]);
      setShowSirenSuggestions(false);
      return;
    }

    setIsLoadingSiren(true);
    try {
      const response = await axios.get(
        `https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(query)}&limite=5`
      );
      setSirenSuggestions(response.data.results || []);
      setShowSirenSuggestions(true);
    } catch (error) {
      console.error('Error searching SIREN:', error);
    } finally {
      setIsLoadingSiren(false);
    }
  };

  const selectSiren = (company) => {
    setFormData({
      ...formData,
      siren: company.siren,
      nom_entreprise: company.nom_complet,
      adresse: company.siege?.adresse || formData.adresse
    });
    setShowSirenSuggestions(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'nouveau': return 'bg-blue-100 text-blue-800';
      case 'rdv_planifie': return 'bg-orange-100 text-orange-800';
      case 'converti_client': return 'bg-green-100 text-green-800';
      case 'abandonne': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Fonctions pour la création de RDV
  const openRdvDialog = (prospect) => {
    setSelectedProspectForRdv(prospect);
    setRdvFormData({
      prospect_id: prospect.id,
      date_rdv: '',
      duree_minutes: 60,
      type_rdv: 'visite_commerciale',
      status: 'planifie',
      commentaire_rdv: ''
    });
    setShowRdvDialog(true);
  };

  const openCreneauxDialog = () => {
    if (!selectedProspectForRdv || !selectedProspectForRdv.latitude || !selectedProspectForRdv.longitude) {
      toast.error('Adresse du prospect non géolocalisée. Impossible de calculer les créneaux optimaux.');
      return;
    }
    setShowCreneauxDialog(true);
  };

  const handleSelectCreneau = (creneauData) => {
    setRdvFormData({
      ...rdvFormData,
      date_rdv: creneauData.date_rdv.slice(0, 16), // Format pour input datetime-local
    });
    
    toast.success(`Créneau sélectionné: ${new Date(creneauData.date_rdv).toLocaleDateString('fr-FR')} à ${new Date(creneauData.date_rdv).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})} avec ${creneauData.suggested_commercial.first_name} ${creneauData.suggested_commercial.last_name}`);
  };

  const handleCreateRdv = async () => {
    if (isSubmitting) return;
    try {
      if (!rdvFormData.prospect_id || !rdvFormData.date_rdv) {
        toast.error('Prospect et date sont obligatoires');
        return;
      }
      setIsSubmitting(true);

      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/api/rendez-vous`, rdvFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await axios.put(`${API}/api/prospects/${selectedProspectForRdv.id}`, {
        ...selectedProspectForRdv,
        status: 'rdv_planifie'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('🎉 Rendez-vous créé avec succès !');
      setShowRdvDialog(false);
      setShowCreneauxDialog(false);
      setSelectedProspectForRdv(null);
      resetRdvForm();
      fetchProspects();
      
    } catch (error) {
      console.error('Error creating rendez-vous:', error);
      toast.error('Erreur lors de la création du rendez-vous');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetRdvForm = () => {
    setRdvFormData({
      prospect_id: '',
      date_rdv: '',
      duree_minutes: 60,
      type_rdv: 'visite_commerciale',
      status: 'planifie',
      commentaire_rdv: ''
    });
  };

  // Fonction de filtrage des prospects
  const getFilteredProspects = () => {
    let filtered = [...prospects];

    // Filtre par utilisateur (télépro/confirmateur)
    if (userFilter) {
      filtered = filtered.filter(p => p.created_by === userFilter || p.assigned_telepro === userFilter);
    }
    // Filtre par commercial
    if (commercialFilter) {
      filtered = filtered.filter(p => p.assigned_commercial === commercialFilter);
    }
    // Filtre par source prospect
    if (sourceFilter) {
      filtered = filtered.filter(p => p.source_prospect === sourceFilter);
    }

    // Filtre par recherche (nom, prénom, entreprise, SIREN, téléphone)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(prospect =>
        prospect.nom?.toLowerCase().includes(search) ||
        prospect.prenom?.toLowerCase().includes(search) ||
        prospect.nom_entreprise?.toLowerCase().includes(search) ||
        prospect.siren?.includes(search) ||
        prospect.telephone?.includes(search) ||
        prospect.adresse?.toLowerCase().includes(search)
      );
    }

    // Filtre par statut
    if (statusFilter) {
      const rdvStatuses = ['confirme', 'realise', 'reporte', 'annule', 'signe', 'r2'];
      if (rdvStatuses.includes(statusFilter)) {
        // Filtrer par statut du RDV associé
        filtered = filtered.filter(prospect => {
          const rdv = rendezVousList.find(r => r.prospect_id === prospect.id);
          if (!rdv) return false;
          if (statusFilter === 'confirme') return rdv.confirmed && rdv.status === 'planifie';
          if (statusFilter === 'signe') return rdv.resultat === 'signe';
          if (statusFilter === 'r2') return rdv.resultat && rdv.resultat.startsWith('r2');
          if (statusFilter === 'planifie') return rdv.status === 'planifie' && !rdv.confirmed;
          return rdv.status === statusFilter;
        });
      } else {
        filtered = filtered.filter(prospect => prospect.status === statusFilter);
      }
    }

    // Filtre par date de création
    if (dateFilter !== 'tous') {
      const now = new Date();
      let startDate, endDate;

      switch (dateFilter) {
        case 'ce_mois':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          break;
        case 'mois_dernier':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        case '3_mois':
          startDate = new Date();
          startDate.setMonth(startDate.getMonth() - 3);
          endDate = now;
          break;
        case 'personnalisee':
          if (customDateStart && customDateEnd) {
            startDate = new Date(customDateStart);
            endDate = new Date(customDateEnd);
            endDate.setHours(23, 59, 59, 999);
          }
          break;
      }

      if (startDate && endDate) {
        filtered = filtered.filter(prospect => {
          if (!prospect.created_at) return false;
          const prospectDate = new Date(prospect.created_at);
          return prospectDate >= startDate && prospectDate <= endDate;
        });
      }
    }

    // Tri par date de création (du plus récent au plus ancien)
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });

    return filtered;
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'nouveau': return 'Nouveau';
      case 'rdv_planifie': return 'RDV planifié';
      case 'converti_client': return 'Converti en client';
      case 'abandonne': return 'Abandonné';
      default: return status;
    }
  };

  if (loading || !userLoaded) {
    return <div className="flex justify-center items-center h-64">Chargement...</div>;
  }

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center gap-2">
          <h1 className="text-lg md:text-2xl font-bold truncate">Prospects</h1>
          <div className="flex items-center gap-2">
            {/* Filtre par télépro — admin/confirmateur/administratif */}
            {allUsers.length > 0 && (
              <select value={userFilter} onChange={e => setUserFilter(e.target.value)}
                className="h-8 px-2 text-xs border border-gray-200 rounded-md bg-white max-w-[160px]">
                <option value="">Tous les télépros</option>
                {allUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                ))}
              </select>
            )}
            {allCommerciaux.length > 0 && (
              <select value={commercialFilter} onChange={e => setCommercialFilter(e.target.value)}
                className="h-8 px-2 text-xs border border-gray-200 rounded-md bg-white max-w-[160px]">
                <option value="">Tous les commerciaux</option>
                {allCommerciaux.map(u => (
                  <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                ))}
              </select>
            )}
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button onClick={() => setViewMode('agenda')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors
                  ${viewMode === 'agenda' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                <CalendarDays className="w-4 h-4" /> Agenda
              </button>
              <button onClick={() => { setViewMode('list'); loadRdvIfNeeded(); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors
                  ${viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                <List className="w-4 h-4" /> Liste
              </button>
            </div>
            {viewMode === 'list' && (currentUser?.role === 'telepro' || currentUser?.role === 'admin' || currentUser?.role === 'confirmateur' || currentUser?.role === 'superviseur') && (
              <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" /> Nouveau Prospect
              </Button>
            )}
          </div>
        </div>
        {/* Barre optimisation créneaux — visible en mode agenda */}
        {viewMode === 'agenda' && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <Navigation className="w-4 h-4 text-green-600 shrink-0" />
            <div className="relative flex-1 max-w-lg">
              <input type="text" value={optimAddress}
                onChange={(e) => {
                  setOptimAddress(e.target.value);
                  if (e.target.value.length >= 3) {
                    fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(e.target.value)}&limit=5`)
                      .then(r => r.json()).then(d => setOptimSuggestions(d.features || [])).catch(() => {});
                  } else { setOptimSuggestions([]); }
                }}
                placeholder="Adresse du prospect à placer..."
                className="w-full h-8 px-3 text-sm border border-green-200 rounded-md focus:outline-none focus:ring-1 focus:ring-green-400 bg-white" />
              {optimSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-50 bg-white border rounded-md shadow-lg mt-0.5 max-h-48 overflow-y-auto">
                  {optimSuggestions.map((s, i) => (
                    <button key={i} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                      onClick={() => { setOptimAddress(s.properties.label); setOptimCoords({ lat: s.geometry.coordinates[1], lng: s.geometry.coordinates[0], cp: s.properties.postcode, ville: s.properties.city }); setOptimSuggestions([]); }}>
                      <MapPin className="w-3 h-3 text-gray-400 shrink-0" />{s.properties.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button size="sm" disabled={!optimCoords}
              onClick={() => {
                const params = new URLSearchParams({ address: optimAddress, lat: optimCoords.lat, lng: optimCoords.lng, cp: optimCoords.cp || '', ville: optimCoords.ville || '' });
                window.open(`/creneaux-optimises?${params.toString()}`, '_blank');
              }}
              className="h-8 text-sm bg-green-600 hover:bg-green-700 text-white shrink-0">
              <Zap className="w-4 h-4 mr-1" /> Trouver un Créneau Optimisé
            </Button>
          </div>
        )}
      </div>

      {/* AGENDA VIEW */}
      {viewMode === 'agenda' ? (
        <div style={{ height: 'calc(100vh - 180px)' }}>
          <ProspectCalendar
            onSlotClick={(dateTime) => {
              setRdvFormData(prev => ({ ...prev, date_rdv: dateTime, duree_minutes: 60 }));
              resetForm();
              setRdvFormData(prev => ({ ...prev, date_rdv: dateTime, duree_minutes: 60 }));
              setShowCreateDialog(true);
            }}
            onEditProspect={(prospect) => {
              openEditDialog(prospect);
            }}
            onRefreshNeeded={calendarRefreshKey}
            userFilter={userFilter}
            commercialFilter={commercialFilter}
          />
        </div>
      ) : (
      <>
      {/* LIST VIEW — everything below stays the same */}

      {/* Statistiques rapides */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{prospects.length}</div>
            <div className="text-sm text-gray-600">Total prospects</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">
              {prospects.filter(p => p.status === 'nouveau').length}
            </div>
            <div className="text-sm text-gray-600">Nouveaux</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-orange-600">
              {prospects.filter(p => p.status === 'rdv_planifie').length}
            </div>
            <div className="text-sm text-gray-600">RDV planifiés</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {prospects.filter(p => p.status === 'converti_client').length}
            </div>
            <div className="text-sm text-gray-600">Convertis</div>
          </CardContent>
        </Card>
      </div>

      {/* Recherche et Filtres */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Recherche et Filtres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Recherche */}
            <div className="col-span-2">
              <Label>Rechercher un prospect</Label>
              <Input
                placeholder="Nom, prénom, entreprise, SIREN, téléphone, adresse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Filtre par statut */}
            <div>
              <Label>Statut</Label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Tous les statuts</option>
                <option value="nouveau">Nouveau</option>
                <option value="rdv_planifie">RDV planifié</option>
                <option value="confirme">Confirmé</option>
                <option value="realise">Réalisé</option>
                <option value="reporte">Reporté</option>
                <option value="annule">Annulé</option>
                <option value="signe">Signé</option>
                <option value="r2">R2</option>
                <option value="converti_client">Converti en client</option>
                <option value="abandonne">Abandonné</option>
              </select>
            </div>

            {/* Filtre par source prospect */}
            <div>
              <Label>Source</Label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                <option value="">Toutes les sources</option>
                {prospectSources.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Filtre par période */}
            <div>
              <Label>Période de création</Label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="tous">Toutes les périodes</option>
                <option value="ce_mois">Ce mois</option>
                <option value="mois_dernier">Mois dernier</option>
                <option value="3_mois">3 derniers mois</option>
                <option value="personnalisee">Période personnalisée</option>
              </select>
            </div>
          </div>

          {/* Dates personnalisées */}
          {dateFilter === 'personnalisee' && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={customDateStart}
                  onChange={(e) => setCustomDateStart(e.target.value)}
                />
              </div>
              <div>
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={customDateEnd}
                  onChange={(e) => setCustomDateEnd(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Résultats */}
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              {getFilteredProspects().length} prospect(s) trouvé(s)
              {searchTerm && ` pour "${searchTerm}"`}
            </span>
            {(searchTerm || statusFilter || dateFilter !== 'tous') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setDateFilter('tous');
                  setCustomDateStart('');
                  setCustomDateEnd('');
                }}
              >
                Réinitialiser
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table des prospects */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Prospects</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Adresse</TableHead>
                <TableHead>Code Postal</TableHead>
                <TableHead>Ville</TableHead>
                <TableHead>Énergie</TableHead>
                <TableHead>Statut</TableHead>
                {currentUser?.role === 'admin' && <TableHead>Créé par</TableHead>}
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getFilteredProspects().map((prospect) => (
                <TableRow 
                  key={prospect.id} 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => openEditDialog(prospect)}
                >
                  <TableCell>
                    <div className="text-xs text-gray-500 whitespace-nowrap">
                      {prospect.created_at ? new Date(prospect.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {prospect.prenom} {prospect.nom}
                      </div>
                      <div className="text-sm text-gray-600 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {prospect.telephone}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate max-w-[200px]">{prospect.adresse}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-center">
                      {prospect.code_postal || '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {prospect.ville || '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {prospect.type_energie && (
                        <Badge variant="outline" className="text-xs mb-1">
                          {prospect.type_energie}
                        </Badge>
                      )}
                      {prospect.consommation_annuelle_euro > 0 && (
                        <div className="text-xs text-gray-600">
                          {prospect.consommation_annuelle_euro}€/an
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(prospect.status)}>
                      {getStatusLabel(prospect.status)}
                    </Badge>
                  </TableCell>
                  {currentUser?.role === 'admin' && (
                    <TableCell>
                      <div className="text-sm text-gray-600">
                        {prospect.created_by_name || 'Utilisateur inconnu'}
                      </div>
                    </TableCell>
                  )}
                  <TableCell>
                    {(currentUser?.role === 'telepro' || currentUser?.role === 'admin' || currentUser?.role === 'confirmateur' || currentUser?.role === 'superviseur') && (
                      <div className="flex space-x-1">
                        {prospect.status === 'nouveau' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openRdvDialog(prospect)}
                            className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                          >
                            <Calendar className="w-3 h-3 mr-1" />
                            RDV
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEditDialog(prospect)}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteProspect(prospect.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      </>
      )}
      {/* === FIN BLOC CONDITIONNEL LISTE/AGENDA === */}

      {/* === DIALOGS (accessibles depuis les deux vues) === */}

      {/* Dialog Création de prospect */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouveau Prospect</DialogTitle>
          </DialogHeader>

          {/* Section RDV — En-tête bleu comme Google Agenda */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Planifier un Rendez-vous
              <span className="text-xs font-normal text-blue-500">(optionnel)</span>
            </h3>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Date & Heure</Label>
                <Input type="datetime-local" value={rdvFormData.date_rdv}
                  onChange={e => setRdvFormData({...rdvFormData, date_rdv: e.target.value})}
                  className="bg-white text-sm" />
              </div>
              <div>
                <Label className="text-xs">Durée</Label>
                <select value={rdvFormData.duree_minutes}
                  onChange={e => setRdvFormData({...rdvFormData, duree_minutes: e.target.value})}
                  className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm">
                  <option value={30}>30 min</option>
                  <option value={60}>1 heure</option>
                  <option value={90}>1h30</option>
                  <option value={120}>2 heures</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Type de RDV</Label>
                <select value={rdvFormData.type_rdv}
                  onChange={e => setRdvFormData({...rdvFormData, type_rdv: e.target.value})}
                  className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm">
                  <option value="visite_commerciale">Visite commerciale</option>
                  <option value="appel_telephonique">Appel téléphonique</option>
                  <option value="reunion">Réunion</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Source Prospect *</Label>
                <select value={formData.source_prospect}
                  onChange={e => setFormData({...formData, source_prospect: e.target.value})}
                  className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm">
                  <option value="">Sélectionner...</option>
                  {prospectSources.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Commentaire RDV</Label>
                <Input value={rdvFormData.commentaire_rdv} placeholder="Commentaire..."
                  onChange={e => setRdvFormData({...rdvFormData, commentaire_rdv: e.target.value})}
                  className="bg-white text-sm" />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Informations obligatoires */}
            <div className="space-y-4">
              <h3 className="font-semibold text-red-600">Informations obligatoires</h3>
              
              {/* Adresse avec suggestions */}
              <div className="relative">
                <Label>Adresse *</Label>
                <Input
                  value={formData.adresse}
                  onChange={(e) => {
                    setFormData({...formData, adresse: e.target.value});
                    searchAddresses(e.target.value);
                  }}
                  placeholder="Commencez à taper l'adresse..."
                />
                {showAddressSuggestions && addressSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {addressSuggestions.map((address, index) => (
                      <div
                        key={index}
                        className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() => selectAddress(address)}
                      >
                        {address.properties.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Code postal et Ville */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code postal</Label>
                  <Input
                    value={formData.code_postal}
                    onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                    placeholder="75001"
                  />
                </div>
                <div>
                  <Label>Ville</Label>
                  <Input
                    value={formData.ville}
                    onChange={(e) => setFormData({...formData, ville: e.target.value})}
                    placeholder="Paris"
                  />
                </div>
              </div>

              {/* Lien Google Maps Satellite */}
              {formData.adresse && formData.ville && (
                <button
                  type="button"
                  onClick={() => {
                    const lat = formData.latitude;
                    const lng = formData.longitude;
                    if (lat && lng && lat !== 0 && lng !== 0) {
                      window.open(`https://www.google.com/maps?q=${lat},${lng}&t=k&z=19`, '_blank');
                    } else {
                      const query = [formData.adresse, formData.code_postal, formData.ville].filter(Boolean).join(', ');
                      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`, '_blank');
                    }
                  }}
                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-md transition-colors"
                >
                  <MapPin className="w-4 h-4" />
                  Voir sur Google Maps (vue satellite)
                </button>
              )}
              
              <div>
                <Label>Téléphone *</Label>
                <Input
                  value={formData.telephone}
                  onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  placeholder="01 23 45 67 89"
                />
              </div>
            </div>

            {/* Informations optionnelles */}
            <div className="space-y-4">
              <h3 className="font-semibold text-blue-600">Informations optionnelles</h3>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Prénom</Label>
                  <Input
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Nom</Label>
                  <Input
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>

            </div>

            {/* Informations énergie */}
            <div className="col-span-2 space-y-4">
              <h3 className="font-semibold text-green-600">Informations Énergie</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Type d'énergie</Label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.type_energie}
                    onChange={(e) => setFormData({...formData, type_energie: e.target.value})}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="electricite">Électricité</option>
                    <option value="gaz">Gaz</option>
                    <option value="les_deux">Les deux</option>
                  </select>
                </div>
                
                <div>
                  <Label>Consommation annuelle (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.consommation_annuelle_euro}
                    onChange={(e) => setFormData({...formData, consommation_annuelle_euro: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Consommation annuelle (kWh)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.consommation_annuelle_kwh}
                    onChange={(e) => setFormData({...formData, consommation_annuelle_kwh: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Fournisseur actuel</Label>
                <Input
                  value={formData.fournisseur_actuel}
                  onChange={(e) => setFormData({...formData, fournisseur_actuel: e.target.value})}
                  placeholder="EDF, Engie, Total Energies..."
                />
              </div>

              <div>
                <Label>Notes Télépro</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={2}
                  disabled={currentUser?.role === 'commercial' || currentUser?.role === 'regie'}
                  className={currentUser?.role === 'commercial' || currentUser?.role === 'regie' ? 'bg-gray-100' : ''}
                  placeholder="Notes du télépro..."
                />
              </div>
              <div>
                <Label>Notes Commercial</Label>
                <Textarea
                  value={formData.notes_commercial}
                  onChange={(e) => setFormData({...formData, notes_commercial: e.target.value})}
                  rows={2}
                  disabled={currentUser?.role === 'telepro' || currentUser?.role === 'superviseur'}
                  className={currentUser?.role === 'telepro' || currentUser?.role === 'superviseur' ? 'bg-gray-100' : ''}
                  placeholder="Notes du commercial..."
                />
              </div>
            </div>
            {/* 🆕 NOUVEAUX CHAMPS - Informations Complémentaires */}
            <div className="col-span-2 space-y-4 border-t pt-4">
              <h3 className="font-semibold text-purple-600">Informations Complémentaires</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Situation Familiale</Label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.situation_familiale}
                    onChange={(e) => setFormData({...formData, situation_familiale: e.target.value})}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="celibataire">Célibataire</option>
                    <option value="marie">Marié(e)</option>
                    <option value="pacse">Pacsé(e)</option>
                    <option value="divorce">Divorcé(e)</option>
                    <option value="veuf">Veuf(ve)</option>
                    <option value="concubinage">Concubinage</option>
                  </select>
                </div>

                <div>
                  <Label>Domaine d'activité</Label>
                  <Input
                    value={formData.domaine_activite}
                    onChange={(e) => setFormData({...formData, domaine_activite: e.target.value})}
                    placeholder="BTP, Commerce..."
                  />
                </div>
              </div>

              {/* 🆕 SECTION MONSIEUR */}
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-sm text-blue-900 mb-3">👨 Informations Monsieur</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type de Contrat</Label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={formData.type_contrat_monsieur}
                      onChange={(e) => setFormData({...formData, type_contrat_monsieur: e.target.value})}
                    >
                      <option value="">Sélectionner...</option>
                      <option value="cdi">CDI</option>
                      <option value="cdd">CDD</option>
                      <option value="interim">Intérim</option>
                      <option value="independant">Indépendant</option>
                      <option value="retraite">Retraité</option>
                      <option value="sans_emploi">Sans emploi</option>
                      <option value="etudiant">Étudiant</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>

                  <div>
                    <Label>Revenus Mensuels (€)</Label>
                    <Input
                      type="number"
                      value={formData.revenus_mensuels_monsieur}
                      onChange={(e) => setFormData({...formData, revenus_mensuels_monsieur: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* 🆕 SECTION MADAME */}
              <div className="mt-3 p-3 bg-pink-50 rounded-lg">
                <h4 className="font-medium text-sm text-pink-900 mb-3">👩 Informations Madame</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type de Contrat</Label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={formData.type_contrat_madame}
                      onChange={(e) => setFormData({...formData, type_contrat_madame: e.target.value})}
                    >
                      <option value="">Sélectionner...</option>
                      <option value="cdi">CDI</option>
                      <option value="cdd">CDD</option>
                      <option value="interim">Intérim</option>
                      <option value="independant">Indépendant</option>
                      <option value="retraite">Retraité</option>
                      <option value="sans_emploi">Sans emploi</option>
                      <option value="etudiant">Étudiant</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>

                  <div>
                    <Label>Revenus Mensuels (€)</Label>
                    <Input
                      type="number"
                      value={formData.revenus_mensuels_madame}
                      onChange={(e) => setFormData({...formData, revenus_mensuels_madame: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 mt-3">
                <div>
                  <Label>Montant Crédit en Cours Total (€)</Label>
                  <Input
                    type="number"
                    value={formData.montant_credit_en_cours}
                    onChange={(e) => setFormData({...formData, montant_credit_en_cours: e.target.value})}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Mode de Chauffage</Label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.mode_chauffage_actuel}
                    onChange={(e) => setFormData({...formData, mode_chauffage_actuel: e.target.value})}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="gaz">Gaz</option>
                    <option value="electrique">Électrique</option>
                    <option value="fioul">Fioul</option>
                    <option value="bois">Bois</option>
                    <option value="pompe_chaleur">Pompe à chaleur</option>
                    <option value="mixte">Mixte</option>
                  </select>
                </div>

                <div>
                  <Label>Durée RDV (minutes)</Label>
                  <Input
                    type="number"
                    value={formData.duree_rdv_minutes}
                    onChange={(e) => setFormData({...formData, duree_rdv_minutes: parseInt(e.target.value) || 60})}
                    placeholder="60"
                    min="30"
                    step="15"
                  />
                  <p className="text-xs text-gray-500 mt-1">Pour calcul d'itinéraire (défaut: 60 min)</p>
                </div>
              </div>

              {/* 🆕 TOITURE & QUALIFICATION */}
              <h3 className="font-semibold text-purple-600 pt-2">Toiture & Qualification</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre m² disponibles sur le toit</Label>
                  <Input type="number" value={formData.nombre_m2_toit} placeholder="ex: 40"
                    onChange={(e) => setFormData({...formData, nombre_m2_toit: e.target.value})} />
                </div>
                <div>
                  <Label>Velux ou Chien Assis</Label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.velux_chien_assis}
                    onChange={(e) => setFormData({...formData, velux_chien_assis: e.target.value})}>
                    <option value="">Sélectionner...</option>
                    <option value="Aucun">Aucun</option>
                    <option value="Velux">Velux</option>
                    <option value="Chien assis">Chien assis</option>
                    <option value="Les deux">Les deux</option>
                  </select>
                </div>
                <div>
                  <Label>Type de toiture</Label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.type_toiture}
                    onChange={(e) => setFormData({...formData, type_toiture: e.target.value})}>
                    <option value="">Sélectionner...</option>
                    <option value="Tuiles">Tuiles</option>
                    <option value="Ardoises">Ardoises</option>
                    <option value="Bac acier">Bac acier</option>
                    <option value="Toit plat">Toit plat</option>
                    <option value="Fibrociment">Fibrociment</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                <div>
                  <Label>Orientation du toit</Label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.orientation_toit}
                    onChange={(e) => setFormData({...formData, orientation_toit: e.target.value})}>
                    <option value="">Sélectionner...</option>
                    <option value="Sud">Sud</option>
                    <option value="Sud-Est">Sud-Est</option>
                    <option value="Sud-Ouest">Sud-Ouest</option>
                    <option value="Est">Est</option>
                    <option value="Ouest">Ouest</option>
                    <option value="Nord">Nord</option>
                  </select>
                </div>
                <div>
                  <Label>Zone classée</Label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.zone_classee}
                    onChange={(e) => setFormData({...formData, zone_classee: e.target.value})}>
                    <option value="">Sélectionner...</option>
                    <option value="Oui">Oui</option>
                    <option value="Non">Non</option>
                    <option value="Ne sait pas">Ne sait pas</option>
                  </select>
                </div>
                <div>
                  <Label>Nombre d'enfants à charge</Label>
                  <Input type="number" value={formData.nombre_enfants} placeholder="0" min="0"
                    onChange={(e) => setFormData({...formData, nombre_enfants: e.target.value})} />
                </div>
                <div>
                  <Label>Âge Monsieur</Label>
                  <Input type="number" value={formData.age_monsieur} placeholder="ex: 45" min="18"
                    onChange={(e) => setFormData({...formData, age_monsieur: e.target.value})} />
                </div>
                <div>
                  <Label>Âge Madame</Label>
                  <Input type="number" value={formData.age_madame} placeholder="ex: 42" min="18"
                    onChange={(e) => setFormData({...formData, age_madame: e.target.value})} />
                </div>
                <div>
                  <Label>Montant crédit immobilier (€)</Label>
                  <Input type="number" value={formData.montant_credit_immobilier} placeholder="€"
                    onChange={(e) => setFormData({...formData, montant_credit_immobilier: e.target.value})} />
                </div>
                <div>
                  <Label>Autre crédit</Label>
                  <Input value={formData.autre_credit} placeholder="Détails..."
                    onChange={(e) => setFormData({...formData, autre_credit: e.target.value})} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateProspect} disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700">
              {isSubmitting ? 'Création...' : rdvFormData.date_rdv ? (
                <><Calendar className="w-4 h-4 mr-2" /> Créer Prospect & RDV</>
              ) : (
                <>Créer le Prospect</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Modification de prospect */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le Prospect</DialogTitle>
          </DialogHeader>

          {/* Section RDV — Modifier la date/heure */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Rendez-vous
              {rdvFormData._rdv_id ? <span className="text-xs font-normal text-green-600">(existant)</span> : <span className="text-xs font-normal text-blue-500">(nouveau - optionnel)</span>}
            </h3>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Date & Heure</Label>
                <Input type="datetime-local" value={rdvFormData.date_rdv}
                  onChange={e => setRdvFormData({...rdvFormData, date_rdv: e.target.value})}
                  className="bg-white text-sm" />
              </div>
              <div>
                <Label className="text-xs">Durée</Label>
                <select value={rdvFormData.duree_minutes}
                  onChange={e => setRdvFormData({...rdvFormData, duree_minutes: e.target.value})}
                  className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm">
                  <option value={30}>30 min</option>
                  <option value={60}>1 heure</option>
                  <option value={90}>1h30</option>
                  <option value={120}>2 heures</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Type de RDV</Label>
                <select value={rdvFormData.type_rdv}
                  onChange={e => setRdvFormData({...rdvFormData, type_rdv: e.target.value})}
                  className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm">
                  <option value="visite_commerciale">Visite commerciale</option>
                  <option value="appel_telephonique">Appel téléphonique</option>
                  <option value="reunion">Réunion</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Source Prospect *</Label>
                <select value={formData.source_prospect}
                  onChange={e => setFormData({...formData, source_prospect: e.target.value})}
                  className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm">
                  <option value="">Sélectionner...</option>
                  {prospectSources.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Commentaire RDV</Label>
                <Input value={rdvFormData.commentaire_rdv} placeholder="Commentaire..."
                  onChange={e => setRdvFormData({...rdvFormData, commentaire_rdv: e.target.value})}
                  className="bg-white text-sm" />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Informations obligatoires */}
            <div className="space-y-4">
              <h3 className="font-semibold text-red-600">Informations obligatoires</h3>
              
              {/* Adresse avec suggestions */}
              <div className="relative">
                <Label>Adresse *</Label>
                <Input
                  value={formData.adresse}
                  onChange={(e) => {
                    setFormData({...formData, adresse: e.target.value});
                    searchAddresses(e.target.value);
                  }}
                  placeholder="Commencez à taper l'adresse..."
                />
                {showAddressSuggestions && addressSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {addressSuggestions.map((address, index) => (
                      <div
                        key={index}
                        className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() => selectAddress(address)}
                      >
                        {address.properties.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Code postal et Ville */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code postal</Label>
                  <Input
                    value={formData.code_postal}
                    onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                    placeholder="75001"
                  />
                </div>
                <div>
                  <Label>Ville</Label>
                  <Input
                    value={formData.ville}
                    onChange={(e) => setFormData({...formData, ville: e.target.value})}
                    placeholder="Paris"
                  />
                </div>
              </div>
              
              <div>
                <Label>Téléphone *</Label>
                <Input
                  value={formData.telephone}
                  onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  placeholder="01 23 45 67 89"
                />
              </div>
            </div>

            {/* Informations optionnelles */}
            <div className="space-y-4">
              <h3 className="font-semibold text-blue-600">Informations optionnelles</h3>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Prénom</Label>
                  <Input
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Nom</Label>
                  <Input
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>

              {/* SIREN avec recherche automatique */}
              <SirenSearch
                value={formData.siren}
                onChange={(siren) => setFormData({...formData, siren})}
                onCompanySelect={handleCompanySelect}
              />

              <div>
                <Label>Nom d'entreprise</Label>
                <Input
                  value={formData.nom_entreprise}
                  onChange={(e) => setFormData({...formData, nom_entreprise: e.target.value})}
                />
              </div>
            </div>

            {/* Informations énergie */}
            <div className="col-span-2 space-y-4">
              <h3 className="font-semibold text-green-600">Informations Énergie</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Type d'énergie</Label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.type_energie}
                    onChange={(e) => setFormData({...formData, type_energie: e.target.value})}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="electricite">Électricité</option>
                    <option value="gaz">Gaz</option>
                    <option value="les_deux">Les deux</option>
                  </select>
                </div>
                
                <div>
                  <Label>Consommation annuelle (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.consommation_annuelle_euro}
                    onChange={(e) => setFormData({...formData, consommation_annuelle_euro: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Consommation annuelle (kWh)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.consommation_annuelle_kwh}
                    onChange={(e) => setFormData({...formData, consommation_annuelle_kwh: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Fournisseur actuel</Label>
                <Input
                  value={formData.fournisseur_actuel}
                  onChange={(e) => setFormData({...formData, fournisseur_actuel: e.target.value})}
                  placeholder="EDF, Engie, Total Energies..."
                />
              </div>

              <div>
                <Label>Notes Télépro</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={2}
                  disabled={currentUser?.role === 'commercial' || currentUser?.role === 'regie'}
                  className={currentUser?.role === 'commercial' || currentUser?.role === 'regie' ? 'bg-gray-100' : ''}
                  placeholder="Notes du télépro..."
                />
              </div>
              <div>
                <Label>Notes Commercial</Label>
                <Textarea
                  value={formData.notes_commercial}
                  onChange={(e) => setFormData({...formData, notes_commercial: e.target.value})}
                  rows={2}
                  disabled={currentUser?.role === 'telepro' || currentUser?.role === 'superviseur'}
                  className={currentUser?.role === 'telepro' || currentUser?.role === 'superviseur' ? 'bg-gray-100' : ''}
                  placeholder="Notes du commercial..."
                />
              </div>
            </div>
            {/* 🆕 NOUVEAUX CHAMPS - Informations Complémentaires */}
            <div className="col-span-2 space-y-4 border-t pt-4">
              <h3 className="font-semibold text-purple-600">Informations Complémentaires</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Situation Familiale</Label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.situation_familiale}
                    onChange={(e) => setFormData({...formData, situation_familiale: e.target.value})}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="celibataire">Célibataire</option>
                    <option value="marie">Marié(e)</option>
                    <option value="pacse">Pacsé(e)</option>
                    <option value="divorce">Divorcé(e)</option>
                    <option value="veuf">Veuf(ve)</option>
                    <option value="concubinage">Concubinage</option>
                  </select>
                </div>

                <div>
                  <Label>Domaine d'activité</Label>
                  <Input
                    value={formData.domaine_activite}
                    onChange={(e) => setFormData({...formData, domaine_activite: e.target.value})}
                    placeholder="BTP, Commerce..."
                  />
                </div>
              </div>

              {/* 🆕 SECTION MONSIEUR */}
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-sm text-blue-900 mb-3">👨 Informations Monsieur</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type de Contrat</Label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={formData.type_contrat_monsieur}
                      onChange={(e) => setFormData({...formData, type_contrat_monsieur: e.target.value})}
                    >
                      <option value="">Sélectionner...</option>
                      <option value="cdi">CDI</option>
                      <option value="cdd">CDD</option>
                      <option value="interim">Intérim</option>
                      <option value="independant">Indépendant</option>
                      <option value="retraite">Retraité</option>
                      <option value="sans_emploi">Sans emploi</option>
                      <option value="etudiant">Étudiant</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>

                  <div>
                    <Label>Revenus Mensuels (€)</Label>
                    <Input
                      type="number"
                      value={formData.revenus_mensuels_monsieur}
                      onChange={(e) => setFormData({...formData, revenus_mensuels_monsieur: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* 🆕 SECTION MADAME */}
              <div className="mt-3 p-3 bg-pink-50 rounded-lg">
                <h4 className="font-medium text-sm text-pink-900 mb-3">👩 Informations Madame</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type de Contrat</Label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={formData.type_contrat_madame}
                      onChange={(e) => setFormData({...formData, type_contrat_madame: e.target.value})}
                    >
                      <option value="">Sélectionner...</option>
                      <option value="cdi">CDI</option>
                      <option value="cdd">CDD</option>
                      <option value="interim">Intérim</option>
                      <option value="independant">Indépendant</option>
                      <option value="retraite">Retraité</option>
                      <option value="sans_emploi">Sans emploi</option>
                      <option value="etudiant">Étudiant</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>

                  <div>
                    <Label>Revenus Mensuels (€)</Label>
                    <Input
                      type="number"
                      value={formData.revenus_mensuels_madame}
                      onChange={(e) => setFormData({...formData, revenus_mensuels_madame: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 mt-3">
                <div>
                  <Label>Montant Crédit en Cours Total (€)</Label>
                  <Input
                    type="number"
                    value={formData.montant_credit_en_cours}
                    onChange={(e) => setFormData({...formData, montant_credit_en_cours: e.target.value})}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Mode de Chauffage</Label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.mode_chauffage_actuel}
                    onChange={(e) => setFormData({...formData, mode_chauffage_actuel: e.target.value})}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="gaz">Gaz</option>
                    <option value="electrique">Électrique</option>
                    <option value="fioul">Fioul</option>
                    <option value="bois">Bois</option>
                    <option value="pompe_chaleur">Pompe à chaleur</option>
                    <option value="mixte">Mixte</option>
                  </select>
                </div>

                <div>
                  <Label>Durée RDV (minutes)</Label>
                  <Input
                    type="number"
                    value={formData.duree_rdv_minutes}
                    onChange={(e) => setFormData({...formData, duree_rdv_minutes: parseInt(e.target.value) || 60})}
                    placeholder="60"
                    min="30"
                    step="15"
                  />
                  <p className="text-xs text-gray-500 mt-1">Pour calcul d'itinéraire (défaut: 60 min)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdateProspect} className="bg-blue-600 hover:bg-blue-700">
              Sauvegarder
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Création de RDV avec Créneaux Optimisés */}
      <Dialog open={showRdvDialog} onOpenChange={setShowRdvDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Planifier un Rendez-vous
            </DialogTitle>
            <p className="text-sm text-gray-600">
              {selectedProspectForRdv && (
                `Pour: ${selectedProspectForRdv.prenom} ${selectedProspectForRdv.nom} - ${selectedProspectForRdv.adresse}`
              )}
            </p>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Informations prospect */}
            {selectedProspectForRdv && (
              <Card className="bg-gray-50">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Contact:</span> {selectedProspectForRdv.prenom} {selectedProspectForRdv.nom}
                    </div>
                    <div>
                      <span className="font-medium">Téléphone:</span> {selectedProspectForRdv.telephone}
                    </div>
                    <div className="col-span-2">
                      <span className="font-medium">Adresse:</span> {selectedProspectForRdv.adresse}
                    </div>
                    {selectedProspectForRdv.nom_entreprise && (
                      <div className="col-span-2">
                        <span className="font-medium">Entreprise:</span> {selectedProspectForRdv.nom_entreprise}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Bouton Créneaux Optimisés */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Intelligence Artificielle - Créneaux Optimisés
                  </h3>
                  <p className="text-sm text-blue-600 mt-1">
                    Trouvez automatiquement les meilleurs créneaux en fonction des temps de trajet et de la charge de travail des commerciaux
                  </p>
                </div>
                <Button 
                  onClick={openCreneauxDialog}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={!selectedProspectForRdv?.latitude || !selectedProspectForRdv?.longitude}
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Créneaux Disponibles
                </Button>
              </div>
              
              {(!selectedProspectForRdv?.latitude || !selectedProspectForRdv?.longitude) && (
                <div className="mt-2 text-xs text-orange-600 bg-orange-50 p-2 rounded">
                  ⚠️ Adresse non géolocalisée. L'optimisation des créneaux nécessite une adresse complète.
                </div>
              )}
            </div>

            {/* Formulaire RDV classique */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700">Ou saisir manuellement:</h3>
              
              <div>
                <Label>Date et heure du rendez-vous *</Label>
                <Input
                  type="datetime-local"
                  value={rdvFormData.date_rdv}
                  onChange={(e) => setRdvFormData({...rdvFormData, date_rdv: e.target.value})}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Durée (minutes)</Label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={rdvFormData.duree_minutes}
                    onChange={(e) => setRdvFormData({...rdvFormData, duree_minutes: parseInt(e.target.value)})}
                  >
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 heure</option>
                    <option value={90}>1h30</option>
                    <option value={120}>2 heures</option>
                  </select>
                </div>

                <div>
                  <Label>Type de RDV</Label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={rdvFormData.type_rdv}
                    onChange={(e) => setRdvFormData({...rdvFormData, type_rdv: e.target.value})}
                  >
                    <option value="visite_commerciale">Visite commerciale</option>
                    <option value="telephone">Téléphonique</option>
                    <option value="visio">Visioconférence</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>

              <div>
                <Label>Commentaire</Label>
                <Textarea
                  value={rdvFormData.commentaire_rdv}
                  onChange={(e) => setRdvFormData({...rdvFormData, commentaire_rdv: e.target.value})}
                  placeholder="Notes sur le rendez-vous..."
                  rows={3}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => {
              setShowRdvDialog(false);
              resetRdvForm();
              setSelectedProspectForRdv(null);
            }}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateRdv}
              disabled={!rdvFormData.date_rdv}
              className="bg-green-600 hover:bg-green-700"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Créer le RDV
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Créneaux Disponibles */}
      {selectedProspectForRdv && (
        <CreneauxDisponibles
          isOpen={showCreneauxDialog}
          onClose={() => setShowCreneauxDialog(false)}
          prospectAddress={selectedProspectForRdv.adresse}
          prospectLatitude={selectedProspectForRdv.latitude}
          prospectLongitude={selectedProspectForRdv.longitude}
          onSelectCreneau={handleSelectCreneau}
        />
      )}
    </div>
  );
};

export default ProspectsPage;